/**
 * Typed-ish fetchers for the CARA browse surface —
 * /api/v1/integration/cara/*.
 *
 * Like the Veeva/Blob browse module, the backend returns plain dicts for these
 * reads (they mirror external data), so the response shapes here are
 * hand-written interfaces rather than OpenAPI-generated types.
 *
 * **Every call here is a read.** There is no upload, download or write to add
 * later: the backend cannot express one (ADR-0023 — the CARA library can only
 * issue whitelisted read requests).
 */

import { apiFetch } from "./apiFetch";

/** One CARA object type. Its `name` doubles as the view a search runs against. */
export interface CaraType {
  name: string;
  label: string;
  /** True when the type stores file content. Informational: content download is out of scope. */
  content_enabled: boolean;
}

/** One attribute of a type, in the dotted form a search result uses (`doc.title`). */
export interface CaraField {
  name: string;
  label: string | null;
  data_type: string | null;
  multi_value: boolean;
}

export interface CaraSearchResult {
  rows: Record<string, unknown>[];
  columns: string[];
  total: number | null;
  /** True when the rows stop short of everything that matched. */
  truncated: boolean;
}

/** Operators the backend accepts, mirroring `FILTER_OPERATORS` in the library. */
export const CARA_FILTER_OPERATORS = [
  { value: "eq", label: "equals" },
  { value: "ne", label: "not equals" },
  { value: "gt", label: "greater than" },
  { value: "gte", label: "greater or equal" },
  { value: "lt", label: "less than" },
  { value: "lte", label: "less or equal" },
  { value: "contains", label: "contains" },
] as const;

export type CaraFilterOperator = (typeof CARA_FILTER_OPERATORS)[number]["value"];

export interface CaraFilterSpec {
  field: string;
  operator: CaraFilterOperator | string;
  value: string;
}

export interface CaraSearchInput {
  /** The object type to search. Ignored when `body` is given. */
  typeName?: string | null;
  /** A hand-written CARA search body (JSON), for a search the picker cannot express. */
  body?: string | null;
  text?: string;
  filters?: CaraFilterSpec[];
  /** Columns to keep, in order. Empty means every column the records carry. */
  fields?: string[];
  searchInContent?: boolean;
  limit?: number;
  offset?: number;
}

export function caraListTypes(token: string, connectionId: string): Promise<{ items: CaraType[] }> {
  return apiFetch(`/api/v1/integration/cara/${encodeURIComponent(connectionId)}/types`, {
    token,
  });
}

export function caraDescribeType(
  token: string,
  connectionId: string,
  typeName: string,
): Promise<{ fields: CaraField[] }> {
  return apiFetch(
    `/api/v1/integration/cara/${encodeURIComponent(connectionId)}/types/${encodeURIComponent(
      typeName,
    )}/fields`,
    { token },
  );
}

export function caraSearch(
  token: string,
  connectionId: string,
  input: CaraSearchInput,
): Promise<CaraSearchResult> {
  return apiFetch(`/api/v1/integration/cara/${encodeURIComponent(connectionId)}/search`, {
    method: "POST",
    token,
    json: {
      type_name: input.typeName ?? null,
      body: input.body ?? null,
      text: input.text ?? "",
      filters: input.filters ?? [],
      fields: input.fields ?? [],
      search_in_content: input.searchInContent ?? false,
      limit: input.limit ?? 100,
      offset: input.offset ?? 0,
    },
  });
}
